import React, { useEffect, Suspense, lazy } from "react";
import { Switch, Route, useLocation, Redirect } from "wouter";
import { queryClient, getQueryFn } from "./lib/queryClient";
import { QueryClientProvider, useQueryClient } from "@tanstack/react-query";
import { Toaster } from "@/components/ui/toaster";
import { TooltipProvider } from "@/components/ui/tooltip";
import { SidebarProvider, SidebarTrigger } from "@/components/ui/sidebar";
import { AppSidebar } from "@/components/AppSidebar";
import { ThemeToggle } from "@/components/ThemeToggle";
import { NotificationBell } from "@/components/NotificationBell";
import { OfflineIndicator } from "@/components/OfflineIndicator";
import { PWAInstallPrompt } from "@/components/PWAInstallPrompt";
import { ErrorBoundary } from "@/components/ErrorBoundary";
// Disabled auto-showing location banner
// import { LocationPromptBanner } from "@/components/LocationPromptBanner";
import { AuthProvider, useAuth } from "@/contexts/AuthContext";
import { useAuthStore } from "@/stores/authStore";
import { PlanProvider, usePlan } from "@/contexts/PlanContext";
import { LocationProvider } from "@/contexts/LocationContext";
import { StaffEventsProvider } from "@/contexts/StaffEventsContext";
import { OnboardingTourProvider } from "@/contexts/OnboardingTourContext";
import { OnboardingTour } from "@/components/OnboardingTour";
import { initializeSocket } from "@/lib/socket";
import { RouteLoadingFallback } from "@/components/RouteLoadingFallback";
import { SubscriptionBlockModal } from "@/components/SubscriptionBlockModal";

// Critical routes - loaded eagerly (needed immediately)
import { Login, Signup } from "@/features/auth";
const ResetPassword = lazy(() => import("@/features/auth").then(m => ({ default: m.ResetPassword })));
import DashboardWrapper from "@/components/DashboardWrapper";
import NotFound from "@/pages/not-found";

// Lazy load all other routes for code splitting
const OnboardingWelcome = lazy(() => import("@/features/onboarding").then(m => ({ default: m.OnboardingWelcome })));
const OnboardingSuccess = lazy(() => import("@/features/onboarding").then(m => ({ default: m.OnboardingSuccess })));
const OnboardingCancel = lazy(() => import("@/features/onboarding").then(m => ({ default: m.OnboardingCancel })));
const OnboardingWrapper = lazy(() => import("@/features/onboarding").then(m => ({ default: m.OnboardingWrapper })));
const BusinessDetails = lazy(() => import("@/features/onboarding").then(m => ({ default: m.BusinessDetails })));
const Calendar = lazy(() => import("@/features/calendar").then(m => ({ default: m.Calendar })));
const Messages = lazy(() => import("@/features/messages").then(m => ({ default: m.Messages })));
const Settings = lazy(() => import("@/features/settings").then(m => ({ default: m.Settings })));
const StaffSettings = lazy(() => import("@/features/settings").then(m => ({ default: m.StaffSettings })));
const BusinessSettings = lazy(() => import("@/features/settings").then(m => ({ default: m.BusinessSettings })));
const Integrations = lazy(() => import("@/features/integrations").then(m => ({ default: m.Integrations })));
const FormSubmissions = lazy(() => import("@/features/forms").then(m => ({ default: m.FormSubmissions })));
const MapView = lazy(() => import("@/features/map").then(m => ({ default: m.MapView })));
const Timesheets = lazy(() => import("@/features/staff").then(m => ({ default: m.Timesheets })));
const TeamChat = lazy(() => import("@/features/staff").then(m => ({ default: m.TeamChat })));
const JobTemplates = lazy(() => import("@/features/templates").then(m => ({ default: m.JobTemplates })));
const CreateJobTemplatePage = lazy(() => import("@/features/templates").then(m => ({ default: m.CreateJobTemplatePage })));
const EditJobTemplatePage = lazy(() => import("@/features/templates").then(m => ({ default: m.EditJobTemplatePage })));
const StaffSchedule = lazy(() => import("@/features/staff").then(m => ({ default: m.StaffSchedule })));
const ResponsiveCalendar = lazy(() => import("@/features/calendar").then(m => ({ default: m.ResponsiveCalendar })));
const FormBuilder = lazy(() => import("@/features/forms").then(m => ({ default: m.FormBuilder })));
const QuoteTemplates = lazy(() => import("@/features/templates").then(m => ({ default: m.QuoteTemplates })));
const CreateQuoteTemplatePage = lazy(() => import("@/features/templates").then(m => ({ default: m.CreateQuoteTemplatePage })));
const EditQuoteTemplate = lazy(() => import("@/features/templates").then(m => ({ default: m.EditQuoteTemplate })));
const QuoteList = lazy(() => import("@/features/quotes").then(m => ({ default: m.QuoteList })));
const QuoteDetails = lazy(() => import("@/features/quotes").then(m => ({ default: m.QuoteDetails })));
const QuoteEditor = lazy(() => import("@/features/quotes").then(m => ({ default: m.QuoteEditor })));
const CreateQuote = lazy(() => import("@/features/quotes").then(m => ({ default: m.CreateQuote })));
const CustomerList = lazy(() => import("@/features/customers").then(m => ({ default: m.CustomerList })));
const CustomerSummary = lazy(() => import("@/features/customers").then(m => ({ default: m.CustomerSummary })));
const CustomerDetails = lazy(() => import("@/features/customers").then(m => ({ default: m.CustomerDetails })));
const CreateCustomerPage = lazy(() => import("@/features/customers").then(m => ({ default: m.CreateCustomerPage })));
const EditCustomerPage = lazy(() => import("@/features/customers").then(m => ({ default: m.EditCustomerPage })));
const Jobs = lazy(() => import("@/features/jobs").then(m => ({ default: m.Jobs })));
const JobDetails = lazy(() => import("@/features/jobs").then(m => ({ default: m.JobDetails })));
const CreateJobPage = lazy(() => import("@/features/jobs").then(m => ({ default: m.CreateJobPage })));
const EditJobPage = lazy(() => import("@/features/jobs").then(m => ({ default: m.EditJobPage })));
const InvoiceList = lazy(() => import("@/features/invoices").then(m => ({ default: m.InvoiceList })));
const InvoiceDetails = lazy(() => import("@/features/invoices").then(m => ({ default: m.InvoiceDetails })));
const EditInvoice = lazy(() => import("@/features/invoices").then(m => ({ default: m.EditInvoice })));
const CreateInvoice = lazy(() => import("@/features/invoices").then(m => ({ default: m.CreateInvoice })));
const Items = lazy(() => import("@/features/items").then(m => ({ default: m.Items })));
const EmailSmsTemplates = lazy(() => import("@/features/templates").then(m => ({ default: m.EmailSmsTemplates })));
const CreateTemplatePage = lazy(() => import("@/features/templates").then(m => ({ default: m.CreateTemplatePage })));
const EditTemplatePage = lazy(() => import("@/features/templates").then(m => ({ default: m.EditTemplatePage })));
const FormLibrary = lazy(() => import("@/features/forms").then(m => ({ default: m.FormLibrary })));
const FormFill = lazy(() => import("@/features/forms").then(m => ({ default: m.FormFill })));
const FormSubmissionDetail = lazy(() => import("@/features/forms").then(m => ({ default: m.FormSubmissionDetail })));
const FormAllSubmissions = lazy(() => import("@/features/forms").then(m => ({ default: m.FormAllSubmissions })));
const StaffDashboard = lazy(() => import("@/features/staff").then(m => ({ default: m.StaffDashboard })));
const StaffAvailability = lazy(() => import("@/features/staff").then(m => ({ default: m.StaffAvailability })));
// const NotificationSettings = lazy(() => import("@/features/settings").then(m => ({ default: m.NotificationSettings })));
const AutomationSettings = lazy(() => import("@/features/automation").then(m => ({ default: m.AutomationSettings })));
const BillingSettings = lazy(() => import("@/features/settings").then(m => ({ default: m.BillingSettings })));
const BillingInvoices = lazy(() => import("@/features/settings").then(m => ({ default: m.BillingInvoices })));
const CustomerApproval = lazy(() => import("@/pages/CustomerApproval"));
const ApproveQuote = lazy(() => import("@/pages/ApproveQuote"));
const DataExport = lazy(() => import("@/features/settings").then(m => ({ default: m.DataExport })));
const StaffManagement = lazy(() => import("@/features/staff").then(m => ({ default: m.StaffManagement })));
const PrivacyPolicy = lazy(() => import("@/pages/PrivacyPolicy"));
const TermsOfService = lazy(() => import("@/pages/TermsOfService"));
const Feedback = lazy(() => import("@/features/feedback").then(m => ({ default: m.Feedback })));
const Payments = lazy(() => import("@/features/payments").then(m => ({ default: m.Payments })));
const PaymentSuccess = lazy(() => import("@/pages/PaymentSuccess"));
const PaymentCancelled = lazy(() => import("@/pages/PaymentCancelled"));
const ClientQuote = lazy(() => import("@/pages/ClientQuote"));
const WeekViewPage = lazy(() => import("@/features/calendar").then(m => ({ default: m.WeekViewPage })));
const DayViewPage = lazy(() => import("@/features/calendar").then(m => ({ default: m.DayViewPage })));
const TenantDashboard = lazy(() => import("@/features/dashboard").then(m => ({ default: m.TenantDashboard })));
const BillingPortal = lazy(() => import("@/features/billing").then(m => ({ default: m.BillingPortal })));
const ActivityFeed = lazy(() => import("@/features/activity").then(m => ({ default: m.ActivityFeed })));
const Tracking = lazy(() => import("@/features/tracking").then(m => ({ default: m.Tracking })));
import { PortalProvider } from "@/contexts/PortalContext";
const PortalRoutes = lazy(() => import("@/pages/portal/PortalRoutes"));
const LegalTermsOfService = lazy(() => import("@/pages/legal/TermsOfService"));
const LegalPrivacyPolicy = lazy(() => import("@/pages/legal/PrivacyPolicy"));
const LegalDPA = lazy(() => import("@/pages/legal/DataProcessingAgreement"));
const LegalSLA = lazy(() => import("@/pages/legal/ServiceLevelAgreement"));
const LegalSettings = lazy(() => import("@/features/settings").then(m => ({ default: m.LegalSettings })));
const Inbox = lazy(() => import("@/features/inbox").then(m => ({ default: m.Inbox })));
const InboxDetail = lazy(() => import("@/features/inbox").then(m => ({ default: m.InboxDetail })));
const InboxConversation = lazy(() => import("@/features/inbox").then(m => ({ default: m.InboxConversation })));
const TradeReadyFeedback = lazy(() => import("@/features/feedback").then(m => ({ default: m.TradeReadyFeedback })));
const AcceptInvite = lazy(() => import("@/pages/AcceptInvite"));
const JobScheduler = lazy(() => import("@/features/jobs").then(m => ({ default: m.JobScheduler })));
const SuperAdminDashboard = lazy(() => import("@/features/superAdmin").then(m => ({ default: m.SuperAdminDashboard })));
const TenantsList = lazy(() => import("@/features/superAdmin").then(m => ({ default: m.TenantsList })));
const TenantDetail = lazy(() => import("@/features/superAdmin").then(m => ({ default: m.TenantDetail })));
const RevenueDashboard = lazy(() => import("@/features/superAdmin").then(m => ({ default: m.RevenueDashboard })));
const PaymentAnalytics = lazy(() => import("@/features/superAdmin").then(m => ({ default: m.PaymentAnalytics })));
const AllUsers = lazy(() => import("@/features/superAdmin").then(m => ({ default: m.AllUsers })));
const UserActivity = lazy(() => import("@/features/superAdmin").then(m => ({ default: m.UserActivity })));
const EmailLogs = lazy(() => import("@/features/superAdmin").then(m => ({ default: m.EmailLogs })));
const SmsLogs = lazy(() => import("@/features/superAdmin").then(m => ({ default: m.SmsLogs })));
const CommunicationTemplates = lazy(() => import("@/features/superAdmin").then(m => ({ default: m.CommunicationTemplates })));
const Announcements = lazy(() => import("@/features/superAdmin").then(m => ({ default: m.Announcements })));
const SupportTickets = lazy(() => import("@/features/superAdmin").then(m => ({ default: m.SupportTickets })));
const SetupGuide = lazy(() => import("@/pages/SetupGuide"));

function Router() {
  return (
    <Suspense fallback={<RouteLoadingFallback />}>
      <Switch>
      <Route path="/reset-password" component={ResetPassword} />
      <Route path="/login" component={Login} />
      <Route path="/signup" component={Signup} />
      <Route path="/accept-invite/:token?" component={AcceptInvite} />
      <Route path="/onboarding/welcome" component={OnboardingWelcome} />
      <Route path="/onboarding/business">
        <OnboardingWrapper><BusinessDetails /></OnboardingWrapper>
      </Route>
      <Route path="/onboarding-success" component={OnboardingSuccess} />
      <Route path="/onboarding-cancel" component={OnboardingCancel} />
      <Route path="/privacy" component={PrivacyPolicy} />
      <Route path="/terms" component={TermsOfService} />
      <Route path="/legal/terms" component={LegalTermsOfService} />
      <Route path="/legal/privacy" component={LegalPrivacyPolicy} />
      <Route path="/legal/dpa" component={LegalDPA} />
      <Route path="/legal/sla" component={LegalSLA} />
      <Route path="/settings/legal" component={LegalSettings} />
      <Route path="/approval/:token" component={CustomerApproval} />
      <Route path="/approve/:token" component={ApproveQuote} />
      <Route path="/client/quotes/:publicId" component={ClientQuote} />
      <Route path="/payment-success" component={PaymentSuccess} />
      <Route path="/payment-cancelled" component={PaymentCancelled} />
      <Route path="/" component={DashboardWrapper} />
      <Route path="/customers" component={CustomerList} />
      <Route path="/customers/new" component={CreateCustomerPage} />
      <Route path="/customers/:id/edit" component={EditCustomerPage} />
      <Route path="/customers/:id" component={CustomerSummary} />
      <Route path="/customer/:id" component={CustomerDetails} />
      <Route path="/jobs" component={Jobs} />
      <Route path="/jobs/new" component={CreateJobPage} />
      <Route path="/jobs/:jobId/edit" component={EditJobPage} />
      <Route path="/jobs/:jobId">
        {(params: any) => <Redirect to={`/job/${params.jobId}`} />}
      </Route>
      <Route path="/job/:id" component={JobDetails} />
      <Route path="/quotes" component={QuoteList} />
      <Route path="/quote/new" component={CreateQuote} />
      <Route path="/quotes/:id/edit" component={QuoteEditor} />
      <Route path="/quotes/:id" component={QuoteDetails} />
      <Route path="/invoices" component={InvoiceList} />
      <Route path="/invoice/new" component={CreateInvoice} />
      <Route path="/invoices/:id/edit" component={EditInvoice} />
      <Route path="/invoices/:id" component={InvoiceDetails} />
      <Route path="/items" component={Items} />
      <Route path="/calendar" component={Calendar} />
      <Route path="/calendar/week" component={WeekViewPage} />
      <Route path="/calendar/day/:date" component={DayViewPage} />
      <Route path="/scheduler" component={JobScheduler} />
      <Route path="/map" component={MapView} />
      <Route path="/inbox" component={Inbox} />
      <Route path="/inbox/conversation/:id" component={InboxConversation} />
      <Route path="/inbox/:id" component={InboxDetail} />
      <Route path="/timesheets" component={Timesheets} />
      <Route path="/staff-schedule" component={StaffSchedule} />
      <Route path="/schedule" component={ResponsiveCalendar} />
      <Route path="/team-chat">{() => <Redirect to="/staff-portal/team-chat" />}</Route>
      <Route path="/staff-portal/team-chat" component={TeamChat} />
      <Route path="/staff-portal/feedback" component={Feedback} />
      <Route path="/messages" component={Messages} />
      <Route path="/form-submissions" component={FormSubmissions} />
      <Route path="/job-templates/create" component={CreateJobTemplatePage} />
      <Route path="/job-templates/:id/edit" component={EditJobTemplatePage} />
      <Route path="/job-templates" component={JobTemplates} />
      <Route path="/templates" component={JobTemplates} />
      <Route path="/quote-templates/create" component={CreateQuoteTemplatePage} />
      <Route path="/quote-templates/:id/edit" component={EditQuoteTemplate} />
      <Route path="/quote-templates" component={QuoteTemplates} />
      <Route path="/email-sms-templates/create" component={CreateTemplatePage} />
      <Route path="/email-sms-templates/:id/edit" component={EditTemplatePage} />
      <Route path="/email-sms-templates" component={EmailSmsTemplates} />
      <Route path="/forms/all-submissions" component={FormAllSubmissions} />
      <Route path="/forms/submissions/:id" component={FormSubmissionDetail} />
      <Route path="/forms/:id/preview" component={FormFill} />
      <Route path="/forms/:id/fill" component={FormFill} />
      <Route path="/forms/edit/:id" component={FormBuilder} />
      <Route path="/forms" component={FormLibrary} />
      <Route path="/form-builder" component={FormBuilder} />
      <Route path="/staff-dashboard" component={StaffDashboard} />
      <Route path="/staff-availability" component={StaffAvailability} />
      <Route path="/staff-settings" component={StaffSettings} />
      {/* <Route path="/notification-settings" component={NotificationSettings} /> */}
      <Route path="/automation-settings" component={AutomationSettings} />
      <Route path="/billing-settings" component={BillingSettings} />
      <Route path="/settings/invoices" component={BillingInvoices} />
      <Route path="/data-export" component={DataExport} />
      <Route path="/workdeck-feedback" component={TradeReadyFeedback} />
      <Route path="/setup-guide" component={SetupGuide} />
      <Route path="/team" component={StaffManagement} />
      <Route path="/integrations" component={Integrations} />
      <Route path="/payments" component={Payments} />
      <Route path="/settings/business" component={BusinessSettings} />
      <Route path="/settings" component={Settings} />
      <Route path="/tenant-dashboard" component={TenantDashboard} />
      <Route path="/billing" component={BillingPortal} />
      <Route path="/activity" component={ActivityFeed} />
      <Route path="/tracking" component={Tracking} />
      <Route path="/super-admin/dashboard" component={SuperAdminDashboard} />
      <Route path="/super-admin/tenants" component={TenantsList} />
      <Route path="/super-admin/tenants/:tenantId" component={TenantDetail} />
      <Route path="/super-admin/revenue/dashboard" component={RevenueDashboard} />
      <Route path="/super-admin/revenue/by-plan" component={RevenueDashboard} />
      <Route path="/super-admin/payments/analytics" component={PaymentAnalytics} />
      <Route path="/super-admin/users" component={AllUsers} />
      <Route path="/super-admin/users/activity" component={UserActivity} />
      <Route path="/super-admin/emails" component={EmailLogs} />
      <Route path="/super-admin/sms" component={SmsLogs} />
      <Route path="/super-admin/announcements" component={Announcements} />
      <Route path="/super-admin/templates" component={CommunicationTemplates} />
      <Route path="/super-admin/support-tickets" component={SupportTickets} />
      <Route path="/portal/*">
        {() => (
          <PortalProvider>
            <PortalRoutes />
          </PortalProvider>
        )}
      </Route>
      <Route component={NotFound} />
      </Switch>
    </Suspense>
  );
}

function AuthenticatedApp() {
  const { isAuthenticated, isLoading, user } = useAuth();
  const { subscriptionStatus, isSubscriptionActive, isLoading: isPlanLoading } = usePlan();
  const [location] = useLocation();
  const queryClient = useQueryClient();
  const hasHydrated = useAuthStore((state) => state._hasHydrated);
  const style = {
    "--sidebar-width": "16rem",
    "--sidebar-width-icon": "3rem",
  };

  const isPublicRoute = location.startsWith('/approval/') || location.startsWith('/approve/') || location.startsWith('/client/quotes/') || location === '/login' || location === '/signup' || location.startsWith('/signup/') || location.startsWith('/onboarding/') || location === '/privacy' || location === '/terms' || location.startsWith('/legal/') || location.startsWith('/portal/') || location.startsWith('/accept-invite') || location.startsWith('/reset-password');

  // Initialize WebSocket connection for authenticated, non-public routes
  useEffect(() => {
    if (isAuthenticated && !isPublicRoute) {
      initializeSocket();
    }
  }, [isAuthenticated, isPublicRoute]);

  // Prefetch integration statuses when authenticated (runs once)
  useEffect(() => {
    if (isAuthenticated && !isPublicRoute) {
      // Prefetch all integration statuses in the background
      const integrationQueries = [
        "/xero/status",
        "/stripe-connect/status",
        "/staff/google/status",
        "/staff/outlook/status",
        "/integrations/resend/status",
        "/integrations/gmail/status",
        "/tenant/credentials/xero",
        "/tenant/credentials/stripe",
        "/tenant/credentials/resend",
      ];

      integrationQueries.forEach((queryKey) => {
        queryClient.prefetchQuery({
          queryKey: [queryKey],
          queryFn: getQueryFn({ on401: "throw" }),
        }).catch((err) => {
          // Silently fail prefetch - it's just for optimization
          console.debug(`Failed to prefetch ${queryKey}:`, err);
        });
      });
    }
  }, [isAuthenticated, isPublicRoute]);

  // For public routes, render immediately without waiting for auth
  if (isPublicRoute) {
    return <Router />;
  }

  // Wait for store hydration from localStorage before making routing decisions
  // This prevents redirecting to login before we've loaded persisted auth state
  if (typeof window !== 'undefined' && !hasHydrated) {
    return (
      <SidebarProvider style={style as React.CSSProperties}>
        <div className="flex h-screen w-full">
          <div className="w-[16rem] border-r bg-sidebar animate-pulse" />
          <div className="flex flex-col flex-1 min-w-0">
            <header className="flex items-center justify-between gap-4 p-4 border-b shrink-0">
              <div className="h-6 w-6 bg-muted rounded animate-pulse" />
              <div className="h-8 flex-1 max-w-md bg-muted rounded animate-pulse" />
              <div className="h-6 w-6 bg-muted rounded animate-pulse" />
            </header>
            <main className="flex-1 p-6">
              <div className="space-y-4">
                <div className="h-8 w-64 bg-muted rounded animate-pulse" />
                <div className="h-32 bg-muted rounded animate-pulse" />
              </div>
            </main>
          </div>
        </div>
      </SidebarProvider>
    );
  }

  // On login/signup pages, check store directly (don't wait for API)
  const isLoginOrSignup = location === '/login' || location === '/signup';
  if (isLoginOrSignup) {
    // If we have auth in store, redirect immediately
    if (isAuthenticated && user) {
      let redirectPath = "/";
      if (user.role === "super_admin") {
        redirectPath = "/super-admin/dashboard";
      } else if (user.role === "staff") {
        redirectPath = "/staff-dashboard";
      }
      return <Redirect to={redirectPath} />;
    }
    // Otherwise show login page (don't block on API, don't redirect)
    // This prevents infinite loops after logout
    return <Router />;
  }

  // For protected routes:
  // If authenticated, allow access (dashboard will verify user if needed)
  if (isAuthenticated) {
    // Continue to render app - don't show loading
  } else if (!isLoading) {
    // If not authenticated and done loading, redirect to login
    return <Redirect to="/login" />;
  }

  // Show loading state only if we're loading and not authenticated
  if (isLoading && !isAuthenticated) {
    return (
      <SidebarProvider style={style as React.CSSProperties}>
        <div className="flex h-screen w-full">
          <div className="w-[16rem] border-r bg-sidebar animate-pulse" />
          <div className="flex flex-col flex-1 min-w-0">
            <header className="flex items-center justify-between gap-4 p-4 border-b shrink-0">
              <div className="h-6 w-6 bg-muted rounded animate-pulse" />
              <div className="h-8 flex-1 max-w-md bg-muted rounded animate-pulse" />
              <div className="h-6 w-6 bg-muted rounded animate-pulse" />
            </header>
            <main className="flex-1 p-6">
              <div className="space-y-4">
                <div className="h-8 w-64 bg-muted rounded animate-pulse" />
                <div className="h-32 bg-muted rounded animate-pulse" />
              </div>
            </main>
          </div>
        </div>
      </SidebarProvider>
    );
  }

  // Render layout - sidebar will show correct items based on user role
  // Check subscription status - block access for non-active subscriptions
  // Super admins bypass subscription check
  const isSuperAdmin = user?.role === 'super_admin';
  const shouldBlockAccess = isAuthenticated && !isPlanLoading && !isSubscriptionActive && !isSuperAdmin;

  return (
    <SidebarProvider style={style as React.CSSProperties}>
      <div className="flex h-screen w-full">
        <AppSidebar />
        <div className="flex flex-col flex-1 min-w-0">
          <header className="flex items-center justify-between gap-4 p-4 border-b shrink-0">
            <SidebarTrigger data-testid="button-sidebar-toggle" />
            <div className="flex items-center gap-2">
              {(user?.role === "owner" || user?.role === "admin") && <NotificationBell />}
              <ThemeToggle />
            </div>
          </header>
          <main className="flex-1 overflow-auto p-6">
            <Router />
          </main>
          <OfflineIndicator />
        </div>
      </div>
      {shouldBlockAccess && (
        <SubscriptionBlockModal subscriptionStatus={subscriptionStatus} />
      )}
      <OnboardingTour />
    </SidebarProvider>
  );
}

function App() {
  return (
    <ErrorBoundary>
      <QueryClientProvider client={queryClient}>
        <TooltipProvider>
          <AuthProvider>
            <PlanProvider>
              <LocationProvider>
                <StaffEventsProvider>
                  <OnboardingTourProvider>
                    <AuthenticatedApp />
                  </OnboardingTourProvider>
                  <Toaster />
                  <PWAInstallPrompt />
                </StaffEventsProvider>
              </LocationProvider>
            </PlanProvider>
          </AuthProvider>
        </TooltipProvider>
      </QueryClientProvider>
    </ErrorBoundary>
  );
}

export default App;
